import React, { useMemo, useState } from 'react';
import type { Contact } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useContactDrawerStore } from '@hero/hooks-shared';
import { useContactApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { PencilIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { ContactSummaryTab } from './ContactSummaryTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { ContactEditModal } from '../Modal/ContactEditModal';

export interface ContactDrawerProps {
  syncUrl?: boolean | string;
  /**
   * Whether the drawer is rendered inside a customer-facing or carrier-facing surface.
   * Reserved for parity with `CustomerDrawer` / `SavedLocationDrawer`; ContactDrawer itself
   * has no user-aware sub-content today, but consumers (e.g. CRMPage) thread the value down
   * so inner flows that DO branch on user (e.g. CustomerDrawer opened from a contact link)
   * receive the correct context.
   */
  displayForUser?: 'customer' | 'carrier';
}

interface ContactDrawerContentProps {
  contact: Contact;
  onClose: () => void;
}

const ContactDrawerContent: React.FC<ContactDrawerContentProps> = ({ contact, onClose }) => {
  const { t } = useTranslation(['m-contact']);
  const { isEnabled: canEditContact } = useFeature('contact_module', 'sub_edit_contact');
  const [activeTab, setActiveTab] = useState('summary');
  const [editModalOpen, setEditModalOpen] = useState(false);

  const tabs = [{ id: 'summary', label: t('drawer.tabs.summary') }];

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'edit',
        label: t('drawer.edit'),
        description: t('drawer.edit_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setEditModalOpen(true),
        hide: !canEditContact,
      },
    ],
    [t, canEditContact],
  );

  return (
    <>
      <DrawerHeader title={contact.name || ''} subtitle={contact.phone} onClose={onClose} />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>
        <ContactSummaryTab contact={contact} />
      </DrawerContent>
      <DrawerFooter actions={actions} buttonLabel={t('drawer.actions')} />

      <ContactEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        contactId={contact._id}
      />
    </>
  );
};

export const ContactDrawer: React.FC<ContactDrawerProps> = ({ syncUrl }) => (
  <StackedDrawer<Contact>
    useStore={useContactDrawerStore}
    useItemApi={id => useContactApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="contactId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Contact Details"
    description="View contact information"
    renderContent={(contact, onClose) => (
      <ContactDrawerContent contact={contact} onClose={onClose} />
    )}
  />
);
